// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// UI plumbing: modal system, toasts, download / save-as / share helpers, and a
// small A/B audio player for the original vs cleaned signal.

import { MODALS } from './template';

let toastTimer: number | undefined;

export function toast(message: string): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
  el.classList.add('show');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    el.classList.remove('show');
    window.setTimeout(() => (el.hidden = true), 200);
  }, 2400);
}

// ── Modals ────────────────────────────────────────────────────────────────
export function initModals(root: HTMLElement): void {
  const host = root.querySelector('#modal-host') as HTMLElement;
  const backdrop = root.querySelector('#modal-backdrop') as HTMLElement;
  const closeBtn = root.querySelector('#modal-close') as HTMLElement;

  root.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => openModal(btn.dataset.modal || ''));
  });
  backdrop.addEventListener('click', closeModal);
  closeBtn.addEventListener('click', closeModal);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !host.hidden) closeModal();
  });
}

export function openModal(key: string): void {
  const def = MODALS[key];
  if (!def) return;
  const host = document.getElementById('modal-host') as HTMLElement;
  (document.getElementById('modal-title') as HTMLElement).textContent = def.title;
  (document.getElementById('modal-body') as HTMLElement).innerHTML = def.body;
  host.hidden = false;
  host.classList.add('open');
  (document.getElementById('modal-close') as HTMLElement).focus();
}

export function closeModal(): void {
  const host = document.getElementById('modal-host') as HTMLElement;
  host.classList.remove('open');
  host.hidden = true;
}

// ── File delivery ───────────────────────────────────────────────────────────
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Save-as via the File System Access API, falling back to a normal download. */
export async function saveBlob(
  blob: Blob,
  filename: string,
  description: string,
  ext: string,
  mime: string,
): Promise<'saved' | 'downloaded' | 'cancelled'> {
  const picker = (window as unknown as {
    showSaveFilePicker?: (opts: unknown) => Promise<FileSystemFileHandle>;
  }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [{ description, accept: { [mime]: [ext] } }],
      });
      const writable = await (handle as unknown as {
        createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }>;
      }).createWritable();
      await writable.write(blob);
      await writable.close();
      return 'saved';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
      // fall through to download
    }
  }
  downloadBlob(blob, filename);
  return 'downloaded';
}

export function canShareFiles(files: File[]): boolean {
  const nav = navigator as unknown as { canShare?: (d: { files: File[] }) => boolean; share?: unknown };
  return !!nav.share && !!nav.canShare && nav.canShare({ files });
}

export async function shareFiles(files: File[], title: string, text: string): Promise<boolean> {
  try {
    await (navigator as unknown as { share: (d: unknown) => Promise<void> }).share({
      files,
      title,
      text,
    });
    return true;
  } catch {
    return false;
  }
}

// ── A/B player ──────────────────────────────────────────────────────────────
export type PlayWhich = 'dry' | 'wet';

export class ABPlayer {
  private ctx: AudioContext | null = null;
  private dryBuf: AudioBuffer | null = null;
  private wetBuf: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private playing: PlayWhich | null = null;
  private sampleRate = 48000;

  constructor(private onState: (which: PlayWhich | null) => void) {}

  private context(): AudioContext {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        (window.AudioContext as typeof AudioContext) ||
        ((window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      this.ctx = new Ctor();
    }
    return this.ctx;
  }

  private makeBuffer(samples: Float32Array): AudioBuffer {
    const ctx = this.context();
    const buf = ctx.createBuffer(1, samples.length, this.sampleRate);
    buf.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
    return buf;
  }

  setDry(samples: Float32Array, sampleRate: number): void {
    this.sampleRate = sampleRate;
    this.dryBuf = this.makeBuffer(samples);
  }

  setWet(samples: Float32Array): void {
    this.wetBuf = this.makeBuffer(samples);
  }

  isPlaying(): PlayWhich | null {
    return this.playing;
  }

  async toggle(which: PlayWhich): Promise<void> {
    if (this.playing === which) {
      this.stop();
      return;
    }
    await this.play(which);
  }

  async play(which: PlayWhich): Promise<void> {
    this.stop();
    const ctx = this.context();
    if (ctx.state === 'suspended') await ctx.resume();
    const buf = which === 'dry' ? this.dryBuf : this.wetBuf;
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.onended = () => {
      if (this.source === src) {
        this.source = null;
        this.playing = null;
        this.onState(null);
      }
    };
    src.start();
    this.source = src;
    this.playing = which;
    this.onState(which);
  }

  stop(): void {
    if (this.source) {
      try {
        this.source.onended = null;
        this.source.stop();
      } catch {
        /* already stopped */
      }
      this.source = null;
    }
    if (this.playing) {
      this.playing = null;
      this.onState(null);
    }
  }

  dispose(): void {
    this.stop();
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close().catch(() => {});
    this.ctx = null;
    this.dryBuf = null;
    this.wetBuf = null;
  }
}
